import React, { useRef, useEffect, useState } from 'react';
import { useSearchParams } from "react-router-dom"; // Importar useSearchParams
import { ALLIMAGES_POSITION_URL, NEW_IMAGE_POSITION_URL, DISPLAY_TIME, CHECK_INTERVAL } from '../config/apiConfig';

const ImageCollagePage = () => {
  const [searchParams] = useSearchParams();
  const rotation = searchParams.get("rotation") || "none"; // Valor por defecto: "none"

  const canvasRef = useRef(null);
  const [images, setImages] = useState([]);
  const [normalizedImages, setNormalizedImages] = useState([]);
  const [newImage, setNewImage] = useState(null);
  const [showNewImage, setShowNewImage] = useState(false);
  const [waitingForNewImage, setWaitingForNewImage] = useState(false);

  // Obtener el tamaño de la ventana
  const getWindowSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [canvasSize, setCanvasSize] = useState(getWindowSize());

  useEffect(() => {
    // Actualizar tamaño del canvas al cambiar la ventana
    const handleResize = () => setCanvasSize(getWindowSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Obtener todas las imágenes iniciales
    const fetchImages = async () => {
      try {
        const response = await fetch(ALLIMAGES_POSITION_URL);
        const data = await response.json();
        setImages(data.images);
      } catch (error) {
        console.error('Error al obtener las imágenes:', error);
      }
    };

    fetchImages();
  }, []);

  useEffect(() => {
    if (images.length === 0) return;

    // Obtener valores mínimos y máximos de x e y
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    console.log(images);
    const parsedImages = images.map(({ image, position, creator }) => {
      const [x, y] = position.split(",").map(Number);
      xMin = Math.min(xMin, x);
      xMax = Math.max(xMax, x);
      yMin = Math.min(yMin, y);
      yMax = Math.max(yMax, y);
      return { image, x, y, creator };
    });

    // Normalizar posiciones en el canvas
    const margin = 50;
    const scaleX = (canvasSize.width - 2 * margin) / (xMax - xMin || 1);
    const scaleY = (canvasSize.height - 2 * margin) / (yMax - yMin || 1);

    const normalized = parsedImages.map(({ image, x, y, creator }) => ({
      image,
      x: margin + (x - xMin) * scaleX,
      y: margin + (y - yMin) * scaleY,
      creator,
    }));

    setNormalizedImages(normalized);
  }, [images, canvasSize]);

  // Verificar nueva imagen cada CHECK_INTERVAL segundos, solo si no se está mostrando una
  useEffect(() => {
    const checkForNewImage = async () => {
      if (showNewImage || waitingForNewImage) return;

      try {
        const response = await fetch(NEW_IMAGE_POSITION_URL);
        const newImageData = await response.json();

        // comprobar si se ha devuelto un error, en cuyo caso no se mostrará la imagen
        if (newImageData.error) {
          console.error('Error al obtener la nueva imagen:', newImageData.error);
          return;
        }
        // Si la imagen ya está en el collage, ignorarla
        if (images.some(img => img.image === newImageData.image)) return;

        setNewImage(newImageData);
        setShowNewImage(true);
        setWaitingForNewImage(true);

        // Ocultar la imagen después de DISPLAY_TIME segundos y agregarla al collage
        setTimeout(() => {
          setShowNewImage(false);
          setImages(prevImages => [...prevImages, newImageData]);
          setWaitingForNewImage(false); // Permitir que se busquen nuevas imágenes después de colocar la actual
        }, DISPLAY_TIME * 1000);
      } catch (error) {
        console.error('Error al obtener la nueva imagen:', error);
      }
    };

    const interval = setInterval(checkForNewImage, CHECK_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [images, showNewImage, waitingForNewImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Ajustar tamaño del canvas
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Limpiar canvas antes de redibujar (evita imágenes dobles)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Establecer fondo negro
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const drawRotatedImage = (img, x, y, width, height, angle) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
      ctx.restore();
    };

    // Dibujar imágenes normalizadas
    normalizedImages.forEach(({ image, x, y }) => {
      const img = new Image();
      img.src = image;
      img.onload = () => {
        const maxSize = 150;
        let imgWidth = img.width;
        let imgHeight = img.height;

        if (imgWidth > imgHeight) {
          imgHeight = (imgHeight / imgWidth) * maxSize;
          imgWidth = maxSize;
        } else {
          imgWidth = (imgWidth / imgHeight) * maxSize;
          imgHeight = maxSize;
        }

        let angle = 0;
        if (rotation === "right") angle = Math.PI / 2;
        if (rotation === "left") angle = -Math.PI / 2;

        ctx.globalAlpha = 0.5;
        drawRotatedImage(img, x, y, imgWidth, imgHeight, angle);
        ctx.globalAlpha = 1.0;
      };
    });

    // Dibujar la nueva imagen en grande
    if (showNewImage && newImage) {
      const img = new Image();
      img.src = newImage.image;
      img.onload = () => {
        const maxWidth = canvas.width * 0.75;
        const maxHeight = canvas.height * 0.75;
    
        let imgWidth = img.width;
        let imgHeight = img.height;
    
        // Escalar la imagen manteniendo el aspecto
        if (imgWidth > imgHeight) {
          imgHeight = (imgHeight / imgWidth) * maxWidth;
          imgWidth = maxWidth;
          if (imgHeight > maxHeight) {
            imgWidth = (imgWidth / imgHeight) * maxHeight;
            imgHeight = maxHeight;
          }
        } else {
          imgWidth = (imgWidth / imgHeight) * maxHeight;
          imgHeight = maxHeight;
          if (imgWidth > maxWidth) {
            imgHeight = (imgHeight / imgWidth) * maxWidth;
            imgWidth = maxWidth;
          }
        }
    
        // Dibujar la imagen centrada en el canvas
        let angle = 0;
        if (rotation === "right") angle = Math.PI / 2;
        if (rotation === "left") angle = -Math.PI / 2;

        ctx.globalAlpha = 1.0;
        drawRotatedImage(img, canvas.width / 2, canvas.height / 2, imgWidth, imgHeight, angle);
    
        // Calcular la nueva posición del texto según la rotación
        let textX = canvas.width / 2;
        let textY = (canvas.height / 2) + imgHeight / 2 + 30; // Posición por defecto

        // ctx.fillText(newImage.creator, canvas.width / 2, (canvas.height / 2) + imgHeight / 2 + 30);
        if (rotation === "right") {
          textX = (canvas.width / 2) - imgHeight / 2 - 30; // Ajustar a la derecha de la imagen girada
          textY = canvas.height / 2; 
        } else if (rotation === "left") {
          textX = (canvas.width / 2) + imgHeight / 2 + 30; // Ajustar a la izquierda de la imagen girada
          textY = canvas.height / 2;
        }
        
        // Aplicar rotación al texto de manera correcta
        ctx.save();
        ctx.translate(textX, textY); // Mover el origen de coordenadas al punto donde se dibujará el texto
        ctx.rotate(angle); // Rotar el texto en la misma dirección que la imagen
        ctx.fillStyle = "white";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(newImage.creator, 0, 0); // Dibujar en (0,0) porque el origen ya está trasladado
        ctx.restore();
      };
    }
  }, [normalizedImages, canvasSize, showNewImage, newImage, rotation]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
      }}
    />
  );
};

export default ImageCollagePage;
